import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Webhook, 
  Copy, 
  Check, 
  RefreshCw, 
  Trash2, 
  Clock, 
  FileJson,
  ArrowDownLeft,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface WebhookRequest {
  id: string;
  received_at: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  content_type: string;
}

export const WebhookDebugger = () => {
  const [endpointId, setEndpointId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [requests, setRequests] = useState<WebhookRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<WebhookRequest | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const webhookUrl = endpointId 
    ? `https://insights.itdukes.com/webhooks/${endpointId}`
    : null;
  const realWebhookUrl = endpointId
    ? `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID || "tahuegiwcpijasnkibja"}.supabase.co/functions/v1/webhook-endpoint/${endpointId}`
    : null;

  const createEndpoint = async () => {
    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("webhook-endpoint", {
        body: { action: "create" },
      });

      if (error) throw error;

      if (data?.success) {
        setEndpointId(data.endpointId);
        setExpiresAt(data.expiresAt);
        toast.success("Webhook endpoint created!", {
          description: "Your endpoint is ready to receive webhooks",
        });
      }
    } catch (error) {
      console.error("Error creating endpoint:", error);
      toast.error("Failed to create endpoint");
    } finally {
      setIsCreating(false);
    }
  };

  const fetchRequests = useCallback(async () => {
    if (!endpointId) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("webhook-endpoint", {
        body: { action: "get-requests", endpointId },
      });

      if (error) throw error;

      if (data?.success) {
        const formattedRequests = data.requests.map((req: any) => ({
          id: req.id,
          received_at: req.received_at,
          method: req.method,
          headers: req.headers || {},
          body: req.body || "",
          content_type: req.content_type || "",
        }));
        setRequests(formattedRequests);
        if (formattedRequests.length > 0 && !selectedRequest) {
          setSelectedRequest(formattedRequests[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setIsLoading(false);
    }
  }, [endpointId, selectedRequest]);

  // Auto-refresh requests every 3 seconds
  useEffect(() => {
    if (!endpointId) return;
    
    fetchRequests();
    const interval = setInterval(fetchRequests, 3000);
    return () => clearInterval(interval);
  }, [endpointId, fetchRequests]);

  const copyUrl = async () => {
    if (!realWebhookUrl) return;
    try {
      await navigator.clipboard.writeText(realWebhookUrl);
      setCopied(true);
      toast.success("Webhook URL copied!", {
        description: "The functional endpoint URL has been copied.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy URL");
    }
  };

  const clearRequests = () => {
    setRequests([]);
    setSelectedRequest(null);
    toast.success("Requests cleared locally");
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case "POST": return "bg-green-500/20 text-green-600 border-green-500/30";
      case "GET": return "bg-blue-500/20 text-blue-600 border-blue-500/30";
      case "PUT": return "bg-yellow-500/20 text-yellow-600 border-yellow-500/30";
      case "DELETE": return "bg-red-500/20 text-red-600 border-red-500/30";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const formatExpiresIn = () => {
    if (!expiresAt) return "";
    const expires = new Date(expiresAt);
    const now = new Date();
    const hoursLeft = Math.max(0, Math.round((expires.getTime() - now.getTime()) / (1000 * 60 * 60)));
    return `${hoursLeft}h remaining`;
  };

  // If no endpoint yet, show create button
  if (!endpointId) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <p className="text-muted-foreground">
            Generate a temporary URL to catch and inspect incoming webhooks in real-time.
          </p>
        </div>

        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Webhook className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">Create Webhook Endpoint</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Generate a unique URL that will receive and log all incoming HTTP requests for 24 hours.
            </p>
            <Button onClick={createEndpoint} disabled={isCreating} className="gap-2">
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Webhook className="w-4 h-4" />
                  Generate Webhook URL
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-muted-foreground">
          Send any HTTP request to your webhook URL and it will appear below in real-time.
        </p>
      </div>

      {/* Webhook URL Card */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Webhook className="w-5 h-5 text-primary" />
              <span className="font-medium">Your Webhook URL</span>
              <Badge variant="outline" className="ml-auto">
                {formatExpiresIn()}
              </Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <code className="flex-1 bg-background border rounded-lg px-4 py-2 text-sm font-mono break-all">
                {webhookUrl}
              </code>
              <Button onClick={copyUrl} variant="outline" className="gap-2 shrink-0">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Powered by IT Dukes API • Send any HTTP request (GET, POST, PUT, DELETE) to this URL. The functional URL is copied when you click Copy.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Request List & Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Request List */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowDownLeft className="w-4 h-4" />
                Incoming Requests
                {requests.length > 0 && (
                  <Badge variant="secondary">{requests.length}</Badge>
                )}
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchRequests}
                  disabled={isLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
                </Button>
                {requests.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearRequests}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {requests.length === 0 ? (
              <div className="p-8 text-center border-t">
                <Clock className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  Waiting for incoming webhooks...
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Send a request to your webhook URL above
                </p>
              </div>
            ) : (
              <div className="max-h-[300px] overflow-y-auto">
                {requests.map((req) => (
                  <button
                    key={req.id}
                    onClick={() => setSelectedRequest(req)}
                    className={`w-full p-4 border-t text-left hover:bg-muted/50 transition-colors ${
                      selectedRequest?.id === req.id ? "bg-muted" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={getMethodColor(req.method)}>
                        {req.method}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(req.received_at)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {req.content_type || "No content type"}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Request Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileJson className="w-4 h-4" />
              Request Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {selectedRequest ? (
              <Tabs defaultValue="body" className="w-full">
                <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
                  <TabsTrigger 
                    value="body" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
                  >
                    Body
                  </TabsTrigger>
                  <TabsTrigger 
                    value="headers"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
                  >
                    Headers
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="body" className="p-4 m-0">
                  <pre className="bg-muted p-3 rounded-lg text-xs overflow-auto max-h-[250px] font-mono">
                    {selectedRequest.body || "(empty body)"}
                  </pre>
                </TabsContent>
                <TabsContent value="headers" className="p-4 m-0">
                  <div className="space-y-2 max-h-[250px] overflow-auto">
                    {Object.entries(selectedRequest.headers).map(([key, value]) => (
                      <div key={key} className="flex gap-2 text-sm">
                        <span className="font-medium text-primary shrink-0">{key}:</span>
                        <span className="text-muted-foreground break-all">{value}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="p-8 text-center border-t">
                <FileJson className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  Select a request to view details
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Test Example */}
      <Card className="bg-muted/50">
        <CardContent className="p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-2">Test with curl:</p>
          <code className="block bg-background border rounded-lg px-3 py-2 text-xs font-mono overflow-auto">
            curl -X POST "{webhookUrl}" -H "Content-Type: application/json" -d '{`{"test": "hello"}`}'
          </code>
        </CardContent>
      </Card>
    </div>
  );
};
